import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { LogoMark } from '@/components/Logo';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-paper">
      {/* Header */}
      <header className="bg-white border-b border-line sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <LogoMark size={36} />
            <span className="text-lg font-bold font-display tracking-tight">
              <span className="text-ink">Portfolio</span>
              <span className="text-signal-blue"> Pal</span>
            </span>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-sm text-muted-brand hover:text-ink transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <article className="bg-white rounded-2xl border border-line p-8 md:p-12 shadow-sm">
          <h1 className="text-3xl md:text-4xl font-bold text-ink font-display tracking-tight mb-2">
            Privacy Policy
          </h1>
          <p className="text-muted-brand mb-8">
            <strong>Effective Date:</strong> August 8, 2026
          </p>

          <div className="prose prose-slate max-w-none">
            <p className="text-slate-brand leading-relaxed mb-6">
              Welcome to Portfolio Pal. We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains what information we collect, how we use it, and what rights you have concerning it when you use our web-based rental property management platform.
            </p>

            <h2 className="text-xl font-semibold text-ink font-display mt-8 mb-4">
              Information We Collect
            </h2>
            <p className="text-slate-brand leading-relaxed mb-4">
              When you use Portfolio Pal, we collect the following types of information:
            </p>
            <ul className="list-disc pl-6 space-y-3 text-slate-brand mb-6">
              <li>
                <strong className="text-ink">Account Information:</strong> Email addresses and hashed passwords used for authentication and account creation.
              </li>
              <li>
                <strong className="text-ink">Personal and Property Details:</strong> Names, contact information, and personal details of landlords and named tenants (e.g., lease agreements, tenancy terms).
              </li>
              <li>
                <strong className="text-ink">Financial and Property Reports:</strong> Data regarding rental properties, financial reports, and document parsing data related to property management.
              </li>
              <li>
                <strong className="text-ink">Integrated Data:</strong> Documents, files, or report data imported or uploaded via our Google Drive/Google Sheets integration.
              </li>
            </ul>

            <h2 className="text-xl font-semibold text-ink font-display mt-8 mb-4">
              How We Use Your Information
            </h2>
            <p className="text-slate-brand leading-relaxed mb-4">
              We use the collected information for the following purposes:
            </p>
            <ul className="list-disc pl-6 space-y-3 text-slate-brand mb-6">
              <li>
                <strong className="text-ink">Service Delivery:</strong> To operate the Portfolio Pal platform, including managing multiple user accounts with tiered permission levels.
              </li>
              <li>
                <strong className="text-ink">Communication:</strong> To send administrative information, such as password reset links and service updates.
              </li>
              <li>
                <strong className="text-ink">Feature Functionality:</strong> To parse documents, generate landlord reports, and manage lease agreements.
              </li>
              <li>
                <strong className="text-ink">Security:</strong> To keep our platform secure and prevent unauthorized access.
              </li>
            </ul>

            <h2 className="text-xl font-semibold text-ink font-display mt-8 mb-4">
              Third-Party Services
            </h2>
            <p className="text-slate-brand leading-relaxed mb-4">
              To provide our services, we share your data with trusted third-party vendors. These services only access your data to perform specific tasks on our behalf:
            </p>
            <ul className="list-disc pl-6 space-y-3 text-slate-brand mb-6">
              <li>
                <strong className="text-ink">MongoDB Atlas:</strong> Used as our primary database for securely storing user profiles, hashed passwords, and property data.
              </li>
              <li>
                <strong className="text-ink">Resend API:</strong> Used to facilitate and deliver email communications (such as password resets and system notifications).
              </li>
              <li>
                <strong className="text-ink">Google APIs:</strong> Used to integrate with Google Sheets and Google Drive for importing and exporting report data and documents.
              </li>
            </ul>

            <h2 className="text-xl font-semibold text-ink font-display mt-8 mb-4">
              Data Security
            </h2>
            <p className="text-slate-brand leading-relaxed mb-6">
              We implement appropriate technical and organizational security measures to protect your personal information, including storing passwords strictly in a hashed format. However, please remember that no electronic transmission over the internet or information storage technology can be guaranteed to be 100% secure.
            </p>

            <h2 className="text-xl font-semibold text-ink font-display mt-8 mb-4">
              Your Rights
            </h2>
            <p className="text-slate-brand leading-relaxed mb-6">
              Depending on your location, you may have the right to request access to, correction of, or deletion of the personal data we hold about you.
            </p>

            <h2 className="text-xl font-semibold text-ink font-display mt-8 mb-4">
              Contact Us
            </h2>
            <p className="text-slate-brand leading-relaxed">
              If you have questions or comments about this policy, please contact us at{' '}
              <a 
                href="mailto:williamhouse909@gmail.com" 
                className="text-signal-blue hover:text-anchor-blue transition-colors"
              >
                williamhouse909@gmail.com
              </a>.
            </p>
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-line bg-white">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <LogoMark size={28} />
            <span className="font-semibold font-display text-ink">Portfolio Pal</span>
          </Link>
          <div className="flex items-center gap-6 text-sm text-muted-brand">
            <Link to="/privacy" className="hover:text-ink transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-ink transition-colors">Terms of Service</Link>
          </div>
          <p className="text-sm text-muted-brand">
            © {new Date().getFullYear()} Portfolio Pal
          </p>
        </div>
      </footer>
    </div>
  );
}
